import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';
import { AuthFormHeaderComponent } from 'app/login/auth-form-header/auth-form-header.component';
import { SharedModule } from 'app/shared/shared.module';
import { ForgotPasswordService } from './forgot-password.service';

@Component({
  selector: 'mh-forgot-password',
  standalone: true,
  imports: [SharedModule, ReactiveFormsModule, RouterModule, AuthFormHeaderComponent],
  templateUrl: 'forgot-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ForgotPasswordService],
})
export class ForgotPasswordComponent {
  private readonly forgotPasswordService = inject(ForgotPasswordService);
  private readonly alertService = inject(AlertService);
  private readonly fb = inject(FormBuilder);

  readonly waiting = signal(false);

  readonly form: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
  readonly canSubmit = computed(() => this.formStatus() === 'VALID' && !this.waiting());

  get email() {
    return this.form.get('email');
  }

  saveData() {
    if (!this.canSubmit()) return;

    this.waiting.set(true);
    this.forgotPasswordService
      .recoveryPassword(this.form.value.email)
      .pipe(finalize(() => this.waiting.set(false)))
      .subscribe({
        next: (response) => {
          if (response === true || response?.['404']) {
            this.notify('forgot_password.changed', 'snackbar-panel-success-user-config');
          } else {
            this.notify('errors.login.something_went_wrong', 'snackbar-panel-error-user-config');
          }
        },
        error: () => {
          this.notify('errors.login.something_went_wrong', 'snackbar-panel-error-user-config');
        },
      });
  }

  private notify(key: string, panelClass: string) {
    this.alertService.handleAlert(key, 5000, 'top', 'center', panelClass);
  }
}
